import { Dialog } from "@mui/material"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { useAppContext } from "../contexts/AppContext"
import { isNullOrUndefined } from "../helper/helper"
import { Organisation } from "../models/organisation/OrganisationModelV2"
import "./MillSwitcher.scss"

/**
 * Header control showing the mill currently worked in, and — when the profile
 * carries more than one — a dialog to switch to another one.
 */

type IconProps = {
    size?: number
    color?: string
    strokeWidth?: number
    className?: string
}

const MillIcon: React.FC<IconProps> = ({ size = 16, color = "currentColor", strokeWidth = 1.9, className }) => (
    <svg className={className} width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round">
        <path d="M2 20h20V9l-5 3V9l-5 3V9l-5 3V4H2Z" />
    </svg>
)

const SwitchIcon: React.FC<IconProps> = ({ size = 11 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round">
        <path d="m16 3 4 4-4 4M20 7H4M8 21l-4-4 4-4M4 17h16" />
    </svg>
)

const CloseIcon: React.FC<IconProps> = ({ size = 15 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round">
        <path d="M18 6 6 18M6 6l12 12" />
    </svg>
)

const CheckIcon: React.FC<IconProps> = ({ size = 10 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round">
        <path d="M20 6 9 17l-5-5" />
    </svg>
)

/** District and province, whichever of the two the organisation actually carries. */
function placeOf(org: Organisation): string | undefined {
    const parts = [org.location?.district, org.location?.province].filter(p => !!p)
    return parts.length > 0 ? parts.join(" · ") : undefined
}

type Props = {
    disabled?: boolean
}

export const MillSwitcher: React.FC<Props> = ({ disabled = false }) => {

    const { profile, millSelected, setMillSelected } = useAppContext()
    const { t } = useTranslation()
    const location = useLocation()

    const [isOpen, setIsOpen] = useState(false)

    const mills = useMemo(() => profile?.organisations ?? [], [profile])

    // The dashboard is not scoped to a single mill, so the switcher has nothing to say there.
    const isHidden = isNullOrUndefined(profile) || location.pathname.includes("dashboard")

    useEffect(() => {
        if (isHidden) {
            setIsOpen(false)
        }
    }, [isHidden])

    if (isHidden || !millSelected) {
        return <></>
    }

    const canSwitch = !disabled && mills.length > 1

    const onSelect = (org: Organisation) => {
        if (org.id !== millSelected.id) {
            setMillSelected(org)
        }
        setIsOpen(false)
    }

    const content = <>
        <MillIcon className="mill-switcher__icon" />
        <span className="mill-switcher__body">
            <span className="mill-switcher__label">{t('active_mill').toUpperCase()}</span>
            <span className="mill-switcher__name">{millSelected.name}</span>
        </span>
        {canSwitch &&
            <span className="mill-switcher__badge">
                <SwitchIcon />
                <span>{t('switch').toUpperCase()}</span>
            </span>}
    </>

    return <>
        {canSwitch
            ? <button type="button" className="mill-switcher" title={t('switch_mill')} onClick={() => setIsOpen(true)}>
                {content}
            </button>
            : <div className="mill-switcher">{content}</div>}

        <Dialog open={isOpen} onClose={() => setIsOpen(false)} maxWidth={false} className="mill-dialog">
            <div className="mill-dialog__panel">
                <header className="mill-dialog__head">
                    <div className="mill-dialog__headings">
                        <span className="mill-dialog__kicker">{t('switch_mill').toUpperCase()}</span>
                        <span className="mill-dialog__title">{t('choose_mill_title')}</span>
                    </div>
                    <button type="button" className="mill-dialog__close" onClick={() => setIsOpen(false)} aria-label={t('close')}>
                        <CloseIcon />
                    </button>
                </header>

                <div className="mill-dialog__list">
                    {mills.map(org => {
                        const isCurrent = org.id === millSelected.id
                        const place = placeOf(org)
                        return (
                            <button
                                key={org.id}
                                type="button"
                                className={`mill-dialog__option ${isCurrent ? 'mill-dialog__option--current' : ''}`}
                                aria-current={isCurrent}
                                onClick={() => onSelect(org)}>
                                <span className="mill-dialog__option-icon">
                                    <MillIcon size={19} color="var(--color-primary)" />
                                </span>
                                <span className="mill-dialog__option-body">
                                    <span className="mill-dialog__option-name">{org.name}</span>
                                    {place && <span className="mill-dialog__option-place">{place}</span>}
                                </span>
                                {isCurrent &&
                                    <span className="mill-dialog__current-pill">
                                        <CheckIcon />
                                        <span>{t('current').toUpperCase()}</span>
                                    </span>}
                            </button>
                        )
                    })}
                </div>
            </div>
        </Dialog>
    </>
}
